import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import type { CanonicalVerdict, CorporateActionMarker, EarningsMarker, HitRateResult, InsiderTrade, SignalDecision, Watchlist } from "../api/types";
import { config } from "../config";
import { Info } from "../components/Info";
import { PriceHistoryChart } from "../components/PriceHistoryChart";
import { SignalCatalystChips } from "../components/SignalCatalystChips";
import { StrategyPicker } from "../components/StrategyPicker";
import { SymbolPicker } from "../components/SymbolPicker";

const actionToneVar: Record<SignalDecision["action"], string> = {
  BUY: "var(--up)",
  SELL: "var(--down)",
  HOLD: "var(--neutral)",
};

export function Signals() {
  const [searchParams] = useSearchParams();
  const [watchlist, setWatchlist] = useState<Watchlist | null>(null);
  const [symbol, setSymbol] = useState(searchParams.get("symbol") ?? "BARC.L");
  const [strategy, setStrategy] = useState("sma_crossover");
  const [fast, setFast] = useState(20);
  const [slow, setSlow] = useState(50);
  const [rsiLow, setRsiLow] = useState(30);
  const [rsiHigh, setRsiHigh] = useState(70);
  const [macdFast, setMacdFast] = useState(12);
  const [macdSlow, setMacdSlow] = useState(26);
  const [macdSignal, setMacdSignal] = useState(9);
  const [donchian, setDonchian] = useState(20);
  const [ichimokuTenkan, setIchimokuTenkan] = useState(9);
  const [ichimokuKijun, setIchimokuKijun] = useState(26);
  const [ichimokuSenkouB, setIchimokuSenkouB] = useState(52);
  const [bollingerWindow, setBollingerWindow] = useState(20);
  const [bollingerStd, setBollingerStd] = useState(2.0);
  const [bollingerRsiOversold, setBollingerRsiOversold] = useState(35);
  const [decision, setDecision] = useState<SignalDecision | null>(null);
  const [hitRate, setHitRate] = useState<HitRateResult | null>(null);
  const [hitRateLoading, setHitRateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Multi-strategy consensus: results of running all strategies in
  // parallel against the same symbol so the user gets the same
  // "consensus across strategies" view the Decide page has, but for
  // an arbitrary symbol (not just the cached universes).
  const [multi, setMulti] = useState<MultiResult | null>(null);
  const [multiLoading, setMultiLoading] = useState(false);

  // Canonical verdict — the SAME BUY/WAIT/AVOID + timeline the Decide page
  // produces, computed live for this symbol via the shared compare()
  // pipeline. This is the authoritative answer; the per-strategy card below
  // is the raw attribution that feeds it. Fetched alongside the strategy run.
  const [verdict, setVerdict] = useState<CanonicalVerdict | null>(null);
  const [verdictLoading, setVerdictLoading] = useState(false);
  const [verdictError, setVerdictError] = useState<string | null>(null);

  // Chart overlay data: earnings, corporate actions, insider buys.
  // All three fetched in parallel on symbol change; silent failure on each
  // → chart still renders, just without those particular marker types.
  const [earningsMarkers, setEarningsMarkers] = useState<EarningsMarker[]>([]);
  const [corpActions, setCorpActions] = useState<CorporateActionMarker[]>([]);
  const [insiderBuys, setInsiderBuys] = useState<InsiderTrade[]>([]);

  useEffect(() => {
    api.ukWatchlist().then(setWatchlist).catch(() => {});
  }, []);

  useEffect(() => {
    setEarningsMarkers([]);
    setCorpActions([]);
    setInsiderBuys([]);
    api.earningsMarkers(symbol)
      .then((r) => setEarningsMarkers(r.earnings ?? []))
      .catch(() => {});
    api.corporateActions(symbol)
      .then((r) => setCorpActions(r.actions ?? []))
      .catch(() => {});
    api.insiderBuys(symbol)
      .then((r) => setInsiderBuys(r.trades ?? []))
      .catch(() => {});
  }, [symbol]);

  function paramsFor(): Record<string, number> | null {
    switch (strategy) {
      case "sma_crossover": return { fast, slow };
      case "rsi_mean_reversion": return { low: rsiLow, high: rsiHigh };
      case "macd_signal_cross": return { fast: macdFast, slow: macdSlow, signal: macdSignal };
      case "donchian_breakout": return { lookback: donchian };
      case "ichimoku_cloud":
        return { tenkan: ichimokuTenkan, kijun: ichimokuKijun, senkou_b: ichimokuSenkouB };
      case "bollinger_bounce":
        return { window: bollingerWindow, num_std: bollingerStd, rsi_oversold: bollingerRsiOversold };
      default: return null;
    }
  }

  async function runAllStrategies() {
    setMultiLoading(true);
    setError(null);
    setMulti(null);
    // Kick off the canonical verdict in parallel with the fast per-strategy
    // signals. Prefer the CACHED compare payload (same data + pipeline as
    // Decide → guaranteed consistent, prod-available, instant). If the symbol
    // isn't in any compared universe, fall back to the LIVE sidecar (any
    // symbol, fresher, but needs the analysis sidecar running).
    setVerdict(null);
    setVerdictError(null);
    setVerdictLoading(true);
    api.symbolVerdictCached(symbol)
      .then(setVerdict)
      .catch(() =>
        api.symbolVerdict(symbol)
          .then(setVerdict)
          .catch((e) => setVerdictError(String(e))))
      .finally(() => setVerdictLoading(false));
    // Strategy params reuse the per-strategy state already on the
    // page (fast/slow, RSI bands, MACD triplet, donchian lookback).
    // buy_and_hold takes no params.
    const jobs: { name: string; params: Record<string, number> | null }[] = [
      { name: "buy_and_hold", params: null },
      { name: "sma_crossover", params: { fast, slow } },
      { name: "rsi_mean_reversion", params: { low: rsiLow, high: rsiHigh } },
      { name: "macd_signal_cross", params: { fast: macdFast, slow: macdSlow, signal: macdSignal } },
      { name: "donchian_breakout", params: { lookback: donchian } },
      { name: "ichimoku_cloud", params: { tenkan: ichimokuTenkan, kijun: ichimokuKijun, senkou_b: ichimokuSenkouB } },
      { name: "bollinger_bounce", params: { window: bollingerWindow, num_std: bollingerStd, rsi_oversold: bollingerRsiOversold } },
    ];
    try {
      const results = await Promise.all(
        jobs.map(async (j) => {
          try {
            const d = await api.evaluateSignal({
              symbol,
              provider: config.defaultProvider,
              strategy: j.name,
              lookbackDays: 365,
              params: j.params,
            });
            return { strategy: j.name, decision: d, error: null as string | null };
          } catch (e) {
            return { strategy: j.name, decision: null, error: String(e) };
          }
        }),
      );
      setMulti(buildMultiResult(results));
    } finally {
      setMultiLoading(false);
    }
  }

  async function evaluate() {
    setLoading(true);
    setError(null);
    setHitRate(null);
    const params = paramsFor();
    try {
      const d = await api.evaluateSignal({
        symbol,
        provider: config.defaultProvider,
        strategy,
        lookbackDays: 365,
        params,
      });
      setDecision(d);
      // Kick off the hit-rate in the background — slower, queries 10y.
      setHitRateLoading(true);
      api
        .hitRate({ symbol, provider: config.defaultProvider, strategy, lookbackYears: 10, params })
        .then(setHitRate)
        .catch(() => {})
        .finally(() => setHitRateLoading(false));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  const ind = decision?.indicators;
  const fmt = (n: number | null | undefined, d = 2) =>
    n === null || n === undefined ? "—" : n.toFixed(d);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Research — single-symbol signal</h1>
        <p style={{ color: "var(--text-dim)", margin: "6px 0 0 0", maxWidth: 880, lineHeight: 1.55 }}>
          <strong style={{ color: "var(--text)" }}>What this page does:</strong>{" "}
          Pick a symbol, run every strategy on it, and get the canonical
          BUY / WAIT / AVOID verdict — plus the trade timeline (swing /
          short / long term) and the per-strategy signals behind it. The
          verdict is computed live by the <em>same pipeline the Decide page
          uses</em>, so the two never disagree.
        </p>
        <p style={{ color: "var(--text-muted)", margin: "8px 0 0 0", maxWidth: 880, fontSize: 12, lineHeight: 1.55 }}>
          <strong>Research vs Backtest:</strong> Research answers <em>"what's the
          verdict on this symbol RIGHT NOW?"</em>. Backtest replays a strategy on
          historical data and shows the equity curve over years. Use Research
          for live decisions; Backtest to validate a strategy before trusting it.
        </p>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0 0", maxWidth: 880, fontSize: 12, lineHeight: 1.55 }}>
          <strong>Research vs Decide:</strong> Both now compute the verdict the
          SAME way (the shared multi-strategy pipeline with sentiment, range
          and earnings demotions). Research runs it live the moment you click
          "Run"; Decide serves the cached worker-refresh snapshot (typically
          0–24h old) across a whole universe. Same logic — so any difference is
          purely data freshness (live vs the last snapshot), not a contradiction.
        </p>
      </div>

      {/* Symbol picker on its own row — same pattern as Backtest. The
          autocomplete needs space; the strategy-tuning grid below it
          covers the rest of the inputs. */}
      <section
        className="card"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "12px 14px",
        }}
      >
        <span className="stat-label">Symbol</span>
        <div style={{ maxWidth: 520 }}>
          <SymbolPicker value={symbol} onChange={setSymbol} placeholder="e.g. NVDA, VUKE.L" />
        </div>
        {watchlist && watchlist.items.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span style={{ marginRight: 4 }}>Or pick from watchlist:</span>
            {watchlist.items.slice(0, 8).map((i) => {
              const active = symbol === i.symbol;
              return (
                <button
                  key={i.symbol}
                  type="button"
                  onClick={() => setSymbol(i.symbol)}
                  title={i.label}
                  style={{
                    padding: "2px 8px",
                    fontSize: 11,
                    borderRadius: 999,
                    border: "1px solid var(--border)",
                    boxShadow: active ? "inset 0 0 0 1px var(--accent, #4f8cff)" : "none",
                    background: active ? "var(--bg-hover)" : "transparent",
                    color: active ? "var(--text)" : "var(--text-dim)",
                    cursor: "pointer",
                    lineHeight: 1.4,
                  }}
                >
                  {i.symbol}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section
        className="card"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 14,
          alignItems: "end",
        }}
      >
        <Labelled label="Strategy" help="strategy">
          <StrategyPicker value={strategy} onChange={setStrategy} />
        </Labelled>
        {strategy === "sma_crossover" && (
          <>
            <Labelled label="Fast SMA" help="fast_sma">
              <input type="number" value={fast} onChange={(e) => setFast(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Slow SMA" help="slow_sma">
              <input type="number" value={slow} onChange={(e) => setSlow(Number(e.target.value))} />
            </Labelled>
          </>
        )}
        {strategy === "rsi_mean_reversion" && (
          <>
            <Labelled label="Oversold below" help="rsi14">
              <input type="number" value={rsiLow} onChange={(e) => setRsiLow(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Overbought above" help="rsi14">
              <input type="number" value={rsiHigh} onChange={(e) => setRsiHigh(Number(e.target.value))} />
            </Labelled>
          </>
        )}
        {strategy === "macd_signal_cross" && (
          <>
            <Labelled label="Fast EMA" help="macd_fast">
              <input type="number" value={macdFast} onChange={(e) => setMacdFast(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Slow EMA" help="macd_slow">
              <input type="number" value={macdSlow} onChange={(e) => setMacdSlow(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Signal EMA" help="macd_signal">
              <input type="number" value={macdSignal} onChange={(e) => setMacdSignal(Number(e.target.value))} />
            </Labelled>
          </>
        )}
        {strategy === "donchian_breakout" && (
          <Labelled label="Lookback (bars)" help="donchian_lookback">
            <input type="number" value={donchian} onChange={(e) => setDonchian(Number(e.target.value))} />
          </Labelled>
        )}
        {strategy === "ichimoku_cloud" && (
          <>
            <Labelled label="Tenkan (fast)">
              <input type="number" value={ichimokuTenkan} onChange={(e) => setIchimokuTenkan(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Kijun (base)">
              <input type="number" value={ichimokuKijun} onChange={(e) => setIchimokuKijun(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Senkou B (cloud)">
              <input type="number" value={ichimokuSenkouB} onChange={(e) => setIchimokuSenkouB(Number(e.target.value))} />
            </Labelled>
          </>
        )}
        {strategy === "bollinger_bounce" && (
          <>
            <Labelled label="Window (bars)">
              <input type="number" value={bollingerWindow} onChange={(e) => setBollingerWindow(Number(e.target.value))} />
            </Labelled>
            <Labelled label="Std dev (× σ)">
              <input type="number" step="0.1" value={bollingerStd} onChange={(e) => setBollingerStd(Number(e.target.value))} />
            </Labelled>
            <Labelled label="RSI oversold below">
              <input type="number" value={bollingerRsiOversold} onChange={(e) => setBollingerRsiOversold(Number(e.target.value))} />
            </Labelled>
          </>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            className="primary"
            onClick={runAllStrategies}
            disabled={multiLoading || !symbol.trim()}
            title="Run every registered strategy on this symbol and show the consensus — same view the Decide page produces for cached universes"
          >
            {multiLoading ? "Running strategies…" : "Run all strategies"}
          </button>
          <button
            onClick={evaluate}
            disabled={loading || !symbol.trim()}
            style={{ fontSize: 11, padding: "4px 8px" }}
          >
            {loading ? "Evaluating…" : "Single strategy (advanced)"}
          </button>
        </div>
      </section>

      {error && (
        <div
          className="card"
          style={{ borderColor: "var(--down)", color: "var(--down)", background: "var(--down-soft)" }}
        >
          {error}
        </div>
      )}

      {(verdict || verdictLoading || verdictError) && (
        <CanonicalVerdictCard
          verdict={verdict}
          loading={verdictLoading}
          error={verdictError}
          symbol={symbol}
        />
      )}

      {multi && <MultiStrategyCard result={multi} symbol={symbol} />}

      {/* Price history sits BETWEEN the multi-strategy verdict and
          the single-strategy detail so the user sees the chart
          context for the verdict they just produced. Split-adjusted
          adj_close so 4:1 / 2:1 splits don't read as fake crashes. */}
      {(multi || decision) && symbol && (
        <PriceHistoryChart
          symbol={symbol}
          earnings={earningsMarkers.length > 0 ? earningsMarkers : undefined}
          corporateActions={corpActions.length > 0 ? corpActions : undefined}
          insiderBuys={insiderBuys.length > 0 ? insiderBuys : undefined}
        />
      )}

      {decision && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(280px, 360px) 1fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          <div
            className="card"
            style={{ borderLeft: `4px solid ${actionToneVar[decision.action]}` }}
          >
            <div className="stat-label">Recommendation</div>
            <div
              className="num"
              style={{
                fontSize: 42,
                fontWeight: 700,
                color: actionToneVar[decision.action],
                letterSpacing: "0.04em",
                marginTop: 4,
              }}
            >
              {decision.action}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4, display: "flex", alignItems: "center" }}>
              Confidence <span className="num" style={{ marginLeft: 4 }}>{Math.round(decision.confidence * 100)}%</span>
              <Info k="confidence" />
              {" · "}
              <span style={{ marginLeft: 6 }}>as of <span className="num">{decision.asOf.slice(0, 10)}</span></span>
            </div>
            <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-dim)" }}>
              Strategy: <code>{decision.strategy}</code>
            </div>
            {(decision.suggestedStopLossPct || decision.suggestedTargetPct) && (
              <div style={{ marginTop: 10, fontSize: 13, display: "flex", gap: 14 }}>
                {decision.suggestedStopLossPct && (
                  <span>
                    <span className="stat-label" style={{ display: "block" }}>Stop</span>
                    <span className="down num">−{decision.suggestedStopLossPct}%</span>
                  </span>
                )}
                {decision.suggestedTargetPct && (
                  <span>
                    <span className="stat-label" style={{ display: "block" }}>Target</span>
                    <span className="up num">+{decision.suggestedTargetPct}%</span>
                  </span>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="card">
              <h3 style={{ margin: "0 0 10px 0", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)" }}>
                Why this call
              </h3>
              <div>
                {decision.reasons.map((r, i) => {
                  const tone = toneOf(r, decision.action);
                  return (
                    <div key={i} className={`reason ${tone}`}>
                      <span className="dot" />
                      <span>{r}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Phase C-3 — catalyst overlay. Renders nothing when
                the decision has no active catalysts AND no flag, so
                the visual weight stays out of the way until there's
                something to say. The banner surfaces the
                tech-vs-event divergence (the Ecopetrol moment). */}
            {(decision.catalysts?.length || decision.catalystFlag) && (
              <div className="card">
                <h3 style={{ margin: "0 0 10px 0", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)" }}>
                  Catalysts
                </h3>
                <SignalCatalystChips decision={decision} />
              </div>
            )}
            <div className="card">
              <h3 style={{ margin: "0 0 12px 0", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)" }}>
                Indicators
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 14 }}>
                <Stat label="Last close" value={fmt(ind?.lastClose)} />
                <Stat label="SMA 20" value={fmt(ind?.sma20)} help="fast_sma" />
                <Stat label="SMA 50" value={fmt(ind?.sma50)} help="slow_sma" />
                <Stat label="SMA 200" value={fmt(ind?.sma200)} />
                <Stat label="RSI 14" value={fmt(ind?.rsi14, 1)} help="rsi14" />
                <Stat
                  label="vs 52w high"
                  value={ind?.priceVs52wHighPct != null ? `${fmt(ind.priceVs52wHighPct, 1)}%` : "—"}
                  tone={ind?.priceVs52wHighPct != null ? (ind.priceVs52wHighPct >= -3 ? "up" : "down") : undefined}
                  help="vs_52w"
                />
                <Stat
                  label="vs 52w low"
                  value={ind?.priceVs52wLowPct != null ? `${fmt(ind.priceVs52wLowPct, 1)}%` : "—"}
                  tone={ind?.priceVs52wLowPct != null ? (ind.priceVs52wLowPct > 20 ? "up" : undefined) : undefined}
                  help="vs_52w"
                />
              </div>
            </div>

            <HitRateCard loading={hitRateLoading} result={hitRate} />
          </div>
        </div>
      )}
    </div>
  );
}

function HitRateCard({ loading, result }: { loading: boolean; result: HitRateResult | null }) {
  const pct = (n: number | null | undefined, d = 1) =>
    n === null || n === undefined ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(d)}%`;

  if (!loading && !result) return null;

  return (
    <div className="card">
      <h3
        style={{
          margin: "0 0 12px 0",
          fontSize: 13,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--text-muted)",
          display: "flex",
          alignItems: "center",
        }}
      >
        Historical hit-rate <Info k="win_rate" />
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 400, color: "var(--text-muted)" }}>
          {result ? `${new Date(result.from).getFullYear()}–${new Date(result.to).getFullYear()}` : ""}
        </span>
      </h3>

      {loading && !result && <div style={{ color: "var(--text-dim)" }}>Simulating 10 years of this strategy…</div>}

      {result && result.totalTrades === 0 && (
        <div style={{ color: "var(--text-dim)", fontSize: 13 }}>
          No round-trip trades in the window. Either the strategy never fires (Buy &amp; Hold) or
          the window's too short — increase lookback to see more.
        </div>
      )}

      {result && result.totalTrades > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 14 }}>
          <Stat
            label="Win rate"
            value={`${result.winRatePct.toFixed(0)}%`}
            tone={result.winRatePct >= 55 ? "up" : result.winRatePct < 45 ? "down" : undefined}
            help="win_rate"
          />
          <Stat label="Trades" value={`${result.winners}W / ${result.losers}L`} />
          <Stat label="Avg winner" value={pct(result.avgWinnerPct)} tone="up" />
          <Stat label="Avg loser" value={pct(result.avgLoserPct)} tone="down" />
          <Stat
            label="Expectancy"
            value={pct(result.expectancyPct, 2)}
            tone={result.expectancyPct > 0 ? "up" : "down"}
            help="expectancy"
          />
          <Stat label="Best / worst" value={`${pct(result.bestPct)} / ${pct(result.worstPct)}`} />
          <Stat
            label="Median hold"
            value={`${Math.round(result.medianHoldingDays)}d`}
            help="median_hold"
          />
          <Stat
            label="Cumulative (rough)"
            value={pct(result.totalReturnPct, 0)}
            tone={result.totalReturnPct > 0 ? "up" : "down"}
          />
        </div>
      )}
    </div>
  );
}

function Labelled({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span className="stat-label">
        {label}
        {help && <Info k={help as keyof typeof import("../docs/tooltips").HELP} />}
      </span>
      {children}
    </label>
  );
}

/** Classify a reason as up / down / neutral so the bullet dot matches the tone.
 * Heuristic: keyword match + agreement with the overall BUY/SELL call. */
function toneOf(reason: string, action: SignalDecision["action"]): "up" | "down" | "neutral" {
  const r = reason.toLowerCase();
  if (/triggered buy|up-trend|oversold|bounce|bullish/.test(r)) return "up";
  if (/triggered sell|down-trend|overbought|bearish|pullback/.test(r)) return "down";
  if (/neutral|no fresh signal|52w/.test(r)) return "neutral";
  return action === "BUY" ? "up" : action === "SELL" ? "down" : "neutral";
}

function Stat({ label, value, tone, help }: { label: string; value: string; tone?: "up" | "down"; help?: string }) {
  const colour = tone === "up" ? "var(--up)" : tone === "down" ? "var(--down)" : "var(--text)";
  return (
    <div>
      <div className="stat-label">
        {label}
        {help && <Info k={help as keyof typeof import("../docs/tooltips").HELP} />}
      </div>
      <div className="stat-value" style={{ color: colour }}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canonical verdict banner — the AUTHORITATIVE BUY/WAIT/AVOID + timeline,
// computed live by the SAME compare() pipeline the Decide page uses, so
// Research and Decide can never contradict. The per-strategy card below is
// the raw attribution that feeds this verdict.
// ---------------------------------------------------------------------------
function CanonicalVerdictCard({
  verdict, loading, error, symbol,
}: {
  verdict: CanonicalVerdict | null;
  loading: boolean;
  error: string | null;
  symbol: string;
}) {
  const colour =
    verdict?.bucket === "BUY" ? "var(--up)"
    : verdict?.bucket === "AVOID" ? "var(--down)"
    : "var(--neutral)";

  return (
    <section className="card" style={{ borderLeft: `4px solid ${verdict ? colour : "var(--border)"}` }}>
      <div className="stat-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Verdict
        <Info k="canonical_verdict" />
      </div>

      {loading && !verdict && (
        <div style={{ marginTop: 8, color: "var(--text-dim)", fontSize: 14 }}>
          Computing canonical verdict (full pipeline — backtests + market state + news)… this takes ~15–30s.
        </div>
      )}

      {error && !verdict && (
        <div style={{ marginTop: 8, color: "var(--down)", fontSize: 13 }}>
          Canonical verdict unavailable: {error}
          <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
            The per-strategy signals below are still live. The verdict needs the analysis sidecar running.
          </div>
        </div>
      )}

      {verdict && (
        <>
          <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 30, fontWeight: 700, color: colour, letterSpacing: "0.04em" }}>
              {verdict.bucket ?? "—"}
            </span>
            <span style={{ fontSize: 13, color: "var(--text-dim)" }}>
              <strong>{symbol}</strong>
              {verdict.long_count != null && verdict.total != null && (
                <> · {verdict.long_count} of {verdict.total} strategies long</>
              )}
              {verdict.conviction && <> · conviction {verdict.conviction}</>}
            </span>
            {verdict.horizon_label && (
              <span
                title="Timeline of the best-fit strategy driving this verdict — how long the trade is meant to be held."
                style={{
                  fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999,
                  background: "var(--surface-2, rgba(255,255,255,0.06))", color: "var(--text)", cursor: "help",
                }}
              >
                {verdict.horizon_label}
              </span>
            )}
          </div>

          {verdict.bucket_reason && (
            <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5 }}>
              {verdict.bucket_reason}
            </div>
          )}

          {verdict.earnings_suppressed && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--warn, #d49b00)" }}>
              ⚠ Earnings within 7 days — BUY suppressed to WAIT (post-print gap risk).
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
            Same verdict the Decide page shows — computed live, just now.
          </div>
        </>
      )}
    </section>
  );
}

// Multi-strategy consensus — runs all strategies on the chosen symbol in
// parallel and produces a Decide-page-style verdict for any symbol (not
// just cached universes). Avoids the friction of "pick a strategy, run it,
// pick another, run it again" — the engine isn't a single-strategy tool.
// ---------------------------------------------------------------------------

interface PerStrategyResult {
  strategy: string;
  decision: SignalDecision | null;
  error: string | null;
}

interface MultiResult {
  perStrategy: PerStrategyResult[];
  buys: number;       // strategies firing a fresh BUY today
  sells: number;
  holds: number;      // total HOLD count (holdsIn + holdsOut when split available)
  /** HOLD with the strategy already in a long position — "stay long, no action". */
  holdsIn: number | null;
  /** HOLD with the strategy flat — "stay flat, waiting for a setup". */
  holdsOut: number | null;
  failed: number;
  meanConfidence: number | null;
  consensus: "BUY" | "SELL" | "HOLD" | "MIXED";
  /** Strategies CURRENTLY LONG the symbol (position state, not today's
   *  trade). Same metric the Decide page reports as "N of 7 currently
   *  long". null when the backend payload predates the inPosition field. */
  currentlyLong: number | null;
}

function buildMultiResult(perStrategy: PerStrategyResult[]): MultiResult {
  const buys = perStrategy.filter((r) => r.decision?.action === "BUY").length;
  const sells = perStrategy.filter((r) => r.decision?.action === "SELL").length;
  const holds = perStrategy.filter((r) => r.decision?.action === "HOLD").length;
  const failed = perStrategy.filter((r) => r.decision === null).length;
  const ok = perStrategy.filter((r) => r.decision !== null);
  const meanConf = ok.length === 0
    ? null
    : ok.reduce((s, r) => s + (r.decision!.confidence || 0), 0) / ok.length;
  let consensus: MultiResult["consensus"] = "MIXED";
  // Majority of NON-FAILED votes wins. With ≤3 ok votes the answer
  // is too noisy to call a consensus, so we report MIXED.
  if (ok.length >= 3) {
    if (buys >= Math.ceil(ok.length / 2 + 0.5)) consensus = "BUY";
    else if (sells >= Math.ceil(ok.length / 2 + 0.5)) consensus = "SELL";
    else if (holds + buys >= ok.length - 1 && buys === 0) consensus = "HOLD";
  }
  // Split HOLD into HOLD-IN (already long, no action) and HOLD-OUT
  // (flat, waiting). Without this the header line "1 BUY · 0 SELL ·
  // 6 HOLD" looked contradictory next to "4 of 7 currently long" —
  // 6 HOLD includes both 3 already-long and 3 still-flat strategies.
  // currentlyLong = buys + holdsIn now reconciles in plain view.
  const anyHasInPosition = ok.some((r) => r.decision!.inPosition !== undefined);
  const holdsIn = anyHasInPosition
    ? ok.filter((r) => r.decision!.action === "HOLD" && r.decision!.inPosition === true).length
    : null;
  const holdsOut = anyHasInPosition
    ? ok.filter((r) => r.decision!.action === "HOLD" && r.decision!.inPosition === false).length
    : null;
  const currentlyLong = anyHasInPosition
    ? ok.filter((r) => r.decision!.inPosition === true).length
    : null;
  return {
    perStrategy, buys, sells, holds, holdsIn, holdsOut, failed,
    meanConfidence: meanConf, consensus, currentlyLong,
  };
}

function MultiStrategyCard({ result, symbol }: { result: MultiResult; symbol: string }) {
  const colour =
    result.consensus === "BUY" ? "var(--up)"
    : result.consensus === "SELL" ? "var(--down)"
    : "var(--neutral)";
  return (
    <section className="card" style={{ borderLeft: `4px solid ${colour}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <div>
          <div className="stat-label">Per-strategy signals (live) — attribution behind the verdict</div>
          <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: colour, letterSpacing: "0.04em" }}>
              {result.consensus}
            </span>
            <span style={{ fontSize: 13, color: "var(--text-dim)" }}>
              <strong>{symbol}</strong> · {result.buys} BUY · {result.sells} SELL
              {result.holdsIn != null && result.holdsOut != null ? (
                <>
                  {" "}·{" "}
                  <span
                    title="HOLD-IN: strategy is already long the symbol from a prior BUY and has no fresh action today."
                    style={{ cursor: "help" }}
                  >
                    {result.holdsIn} HOLD-IN
                  </span>
                  {" "}·{" "}
                  <span
                    title="HOLD-OUT: strategy is currently flat and is waiting for a setup; no fresh action today."
                    style={{ cursor: "help" }}
                  >
                    {result.holdsOut} HOLD-OUT
                  </span>
                </>
              ) : (
                <> · {result.holds} HOLD</>
              )}
              {result.failed > 0 && ` · ${result.failed} failed`}
            </span>
            {/* Position-state breakdown. Reconciles with the Decide
                page's "N of 7 currently long" — currentlyLong now
                equals BUY + HOLD-IN, which is visible in the header
                row above. `inPosition` was added to the SignalDecision
                API; pre-update payloads fall back to the single-HOLD
                rendering and hide this line. */}
            {result.currentlyLong != null && (
              <span
                title={
                  `${result.currentlyLong} of ${result.perStrategy.length} ` +
                  `strategies are currently long the symbol = BUY (${result.buys}) ` +
                  `+ HOLD-IN (${result.holdsIn ?? 0}). ` +
                  `The remaining ${result.perStrategy.length - result.currentlyLong} are flat ` +
                  `(SELL + HOLD-OUT).`
                }
                style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 4, display: "block", cursor: "help" }}
              >
                {result.currentlyLong} of {result.perStrategy.length} strategies currently long
                {" "}<span style={{ color: "var(--text-muted)" }}>
                  (= {result.buys} BUY + {result.holdsIn ?? 0} HOLD-IN)
                </span>
              </span>
            )}
          </div>
        </div>
        {result.meanConfidence !== null && (
          <div style={{ textAlign: "right" }}>
            <div className="stat-label">Mean confidence</div>
            <div className="num" style={{ fontSize: 22, color: colour, fontWeight: 600 }}>
              {Math.round(result.meanConfidence * 100)}%
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        {result.perStrategy.map((r) => (
          <div
            key={r.strategy}
            style={{
              padding: "8px 10px",
              border: "1px solid var(--border)",
              borderRadius: 6,
              background: "rgba(0,0,0,0.12)",
            }}
          >
            <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {r.strategy.replace(/_/g, " ")}
            </div>
            {r.decision ? (
              <div style={{ marginTop: 4 }}>
                <span
                  className="num"
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: actionToneVar[r.decision.action],
                    letterSpacing: "0.04em",
                  }}
                >
                  {r.decision.action}
                </span>
                <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-muted)" }}>
                  {Math.round(r.decision.confidence * 100)}% conf
                </span>
                <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4, lineHeight: 1.4 }}>
                  {r.decision.reasons[0] || "—"}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "var(--down)", marginTop: 4 }}>
                Failed: {(r.error || "").slice(0, 80)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-muted)" }}>
        Decision aid, not advice. Runs all strategies in parallel and counts the votes —
        same logic the Decide page uses for cached universes. Use the "Single strategy" button
        below to tune parameters.
      </div>
    </section>
  );
}
